import api from '@/services/api';
import heic2any from 'heic2any';

/**
 * Upload type for unified upload endpoint
 */
export type UploadType = 'product' | 'extra-product' | 'order-preview' | 'banner' | 'product-video';

const HEIC_EXTENSIONS = ['.heic', '.heif'];

const isHeicFile = (file: File): boolean => {
    const lowerName = file.name.toLowerCase();
    return file.type === 'image/heic'
        || file.type === 'image/heif'
        || HEIC_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
};

const convertHeicToJpeg = async (file: File): Promise<File> => {
    const converted = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9,
    });

    const blob = Array.isArray(converted) ? converted[0] : converted;

    if (!(blob instanceof Blob)) {
        throw new Error('HEIC dosyasi JPEG formatina donusturulemedi');
    }

    const baseName = file.name.replace(/\.(heic|heif)$/i, '');
    return new File([blob], `${baseName || 'image'}.jpg`, { type: 'image/jpeg' });
};

const normalizeImageFile = async (file: File): Promise<File> => {
    if (!isHeicFile(file)) {
        return file;
    }

    return convertHeicToJpeg(file);
};

const uploadBase64File = async (
    file: File,
    path: string,
    type: UploadType
): Promise<string> => {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const fileBase64 = e.target?.result as string;

                const response = await api.post('/admin/upload-image', {
                    fileBase64,
                    imageBase64: fileBase64,
                    path,
                    type,
                });

                resolve(response.data.url);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error('Dosya okunamadı'));
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Backend API üzerinden Storage'a resim yükler (V2 - Unified Endpoint)
 * @param file Yüklenecek dosya
 * @param path Storage path (örn: 'products', 'extra-products/123', 'orders/456/preview')
 * @param type Upload type ('product' | 'extra-product' | 'order-preview')
 * @returns Public download URL
 */
export const uploadImageToStorageV2 = async (
    file: File,
    path: string,
    type: UploadType
): Promise<string> => {
    try {
        const normalizedFile = await normalizeImageFile(file);
        return uploadBase64File(normalizedFile, path, type);
    } catch (error) {
        console.error('Storage upload error:', error);
        throw new Error('Görsel yüklenemedi');
    }
};

export const uploadFileToStorageV2 = async (
    file: File,
    path: string,
    type: UploadType
): Promise<string> => {
    try {
        return uploadBase64File(file, path, type);
    } catch (error) {
        console.error('Storage upload error:', error);
        throw new Error('Dosya yüklenemedi');
    }
};

/**
 * Backend API üzerinden Storage'a resim yükler (V1 - Deprecated)
 * @deprecated Use uploadImageToStorageV2 instead
 * @param file Yüklenecek dosya
 * @param path Storage path (örn: 'products', 'orders/preview')
 * @returns Public download URL
 */
export const uploadImageToStorage = async (file: File, path: string): Promise<string> => {
    try {
        // Dosyayı base64'e çevir
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
            reader.onload = async (e) => {
                try {
                    const imageBase64 = e.target?.result as string;

                    // Backend API'ye gönder (genel upload endpoint - tüm modüller için)
                    const response = await api.post('/admin/products/upload-image', {
                        imageBase64,
                        path,
                    });

                    resolve(response.data.url);
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error('Dosya okunamadı'));
            };

            reader.readAsDataURL(file);
        });
    } catch (error) {
        console.error('Storage upload error:', error);
        throw new Error('Görsel yüklenemedi');
    }
};

/**
 * Base64 string'i File'a çevirir
 * @param base64 Base64 string (data:image/png;base64,...)
 * @param fileName Dosya adı
 * @returns File objesi
 */
export const base64ToFile = (base64: string, fileName: string = 'image.jpg'): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
};

/**
 * Storage'dan resim siler (backend üzerinden)
 * @param imageUrl Silinecek resmin URL'i
 */
export const deleteImageFromStorage = async (imageUrl: string): Promise<void> => {
    try {
        console.log('Deleting image:', imageUrl);

        const response = await api.delete('/admin/products/delete-image', {
            data: { imageUrl },
        });

        console.log('Delete response:', response.data);
    } catch (error: any) {
        console.error('Storage delete error:', error);
        // Hata fırlat ki çağıran yer bilsin
        throw new Error(error.response?.data?.error || 'Görsel silinemedi');
    }
};

/**
 * Birden fazla resmi Storage'a yükler (V2 - Unified Endpoint)
 * @param files Yüklenecek dosyalar
 * @param path Storage path
 * @param type Upload type ('product' | 'extra-product' | 'order-preview')
 * @returns Public download URL'ler
 */
export const uploadMultipleImagesV2 = async (
    files: File[],
    path: string,
    type: UploadType
): Promise<string[]> => {
    const uploadPromises = files.map(file => uploadImageToStorageV2(file, path, type));
    return Promise.all(uploadPromises);
};

/**
 * Birden fazla resmi Storage'a yükler (V1 - Deprecated)
 * @deprecated Use uploadMultipleImagesV2 instead
 * @param files Yüklenecek dosyalar
 * @param path Storage path
 * @returns Public download URL'ler
 */
export const uploadMultipleImages = async (files: File[], path: string): Promise<string[]> => {
    const uploadPromises = files.map(file => uploadImageToStorage(file, path));
    return Promise.all(uploadPromises);
};
